// Card — based on Tremor v1.0.0 with a console-style restyle.
//
// Borderless tinted panel that reads as a card without an outline:
//   - rounded-2xl (16px) for a confident, friendly corner
//   - bg-stone-100 on light gives ~1 shade contrast against the
//     stone-50 page background, enough to define each panel without
//     adding a visible border line
//   - bg-white/[0.04] on dark sits a notch brighter than the page's
//     near-black so panels separate cleanly
//   - p-5 padding kept so existing content layouts don't shift
//
// Pages still pass `className="!p-0"` for table-only cards (rows need
// to touch the edges). `twMerge` resolves the conflict correctly so
// pages that override padding / corner / colour keep working.

import React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cx } from "../../utils/cx";

interface CardProps extends React.ComponentPropsWithoutRef<"div"> {
  asChild?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : "div";
    return (
      <Component
        ref={forwardedRef}
        className={cx(
          "relative w-full overflow-hidden rounded-2xl p-5 text-left",
          "bg-stone-100 dark:bg-white/[0.04]",
          className,
        )}
        tremor-id="tremor-raw"
        {...props}
      />
    );
  },
);

Card.displayName = "Card";

export { Card, type CardProps };
